import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Horizontal swipe gesture for media carousels (posts / events / blogs, both
 * listing cards and detail screens).
 *
 * Emits `swipeLeft` (finger moved left → advance) / `swipeRight` (go back) with
 * the host element, so masonry pages can re-span the card the gesture happened in.
 *
 * Behaviour worth knowing about:
 * - Axis is locked on the first few px of movement: a mostly-vertical drag is
 *   left alone so the page still scrolls normally.
 * - While dragging horizontally the host follows the finger (damped + clamped)
 *   and springs back on release — the gesture reads as a real carousel rather
 *   than an instant jump.
 * - The synthetic `click` a touch produces is swallowed after a horizontal drag.
 *   Listing cards wrap their carousel in a card-level (click)="navigate…", so
 *   without this every swipe would navigate away instead of changing the slide.
 * - Gestures that start on native controls (video/audio, buttons, links) are
 *   ignored so scrubbing a video still works.
 */
@Directive({
  selector: '[appSwipe]',
  standalone: true,
})
export class SwipeDirective implements AfterViewInit, OnDestroy {
  /** Set true to turn the gesture off (e.g. single-media carousels). */
  @Input() swipeDisabled = false;

  @Output() swipeLeft = new EventEmitter<HTMLElement>();
  @Output() swipeRight = new EventEmitter<HTMLElement>();

  /** px of horizontal travel before a drag counts as a slide change. */
  private static readonly THRESHOLD = 45;
  /** px of movement before the gesture commits to an axis. */
  private static readonly AXIS_LOCK = 8;
  /** How much of the finger travel the host actually moves. */
  private static readonly DRAG_DAMPING = 0.35;
  /** Cap on the follow-the-finger offset, px. */
  private static readonly DRAG_MAX = 64;
  private static readonly SPRING_BACK_MS = 280;
  private static readonly SPRING_BACK = `transform ${SwipeDirective.SPRING_BACK_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`;

  private readonly isBrowser: boolean;
  private startX = 0;
  private startY = 0;
  private axis: 'x' | 'y' | null = null;
  private tracking = false;
  /** True once a drag offset has actually been written to the host. */
  private offsetApplied = false;
  private clickGuardTimer?: ReturnType<typeof setTimeout>;
  private settleTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    const host = this.el.nativeElement;
    // Vertical scrolling stays with the browser; horizontal is ours.
    host.style.touchAction = 'pan-y';

    this.zone.runOutsideAngular(() => {
      host.addEventListener('touchstart', this.onTouchStart, { passive: true });
      host.addEventListener('touchmove', this.onTouchMove, { passive: false });
      host.addEventListener('touchend', this.onTouchEnd, { passive: true });
      host.addEventListener('touchcancel', this.onTouchCancel, { passive: true });
      host.addEventListener('click', this.onClickCapture, true);
    });
  }

  ngOnDestroy(): void {
    if (!this.isBrowser) return;
    const host = this.el.nativeElement;
    host.removeEventListener('touchstart', this.onTouchStart);
    host.removeEventListener('touchmove', this.onTouchMove);
    host.removeEventListener('touchend', this.onTouchEnd);
    host.removeEventListener('touchcancel', this.onTouchCancel);
    host.removeEventListener('click', this.onClickCapture, true);
    if (this.clickGuardTimer) clearTimeout(this.clickGuardTimer);
    if (this.settleTimer) clearTimeout(this.settleTimer);
  }

  private onTouchStart = (e: TouchEvent): void => {
    if (this.swipeDisabled || e.touches.length !== 1) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest('video, audio, button, a, input, textarea')) return;

    this.tracking = true;
    this.axis = null;
    this.startX = e.touches[0].clientX;
    this.startY = e.touches[0].clientY;
    if (this.settleTimer) clearTimeout(this.settleTimer);
  };

  private onTouchMove = (e: TouchEvent): void => {
    if (!this.tracking) return;
    const dx = e.touches[0].clientX - this.startX;
    const dy = e.touches[0].clientY - this.startY;

    if (this.axis === null) {
      if (Math.abs(dx) < SwipeDirective.AXIS_LOCK && Math.abs(dy) < SwipeDirective.AXIS_LOCK) return;
      this.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (this.axis === 'y') {
        this.tracking = false;
        return;
      }
      this.el.nativeElement.style.transition = 'none';
    }

    // Horizontal: keep the page from scrolling under the gesture.
    e.preventDefault();
    const offset = Math.max(
      -SwipeDirective.DRAG_MAX,
      Math.min(SwipeDirective.DRAG_MAX, dx * SwipeDirective.DRAG_DAMPING),
    );
    this.el.nativeElement.style.transform = `translate3d(${offset}px, 0, 0)`;
    this.offsetApplied = true;
  };

  private onTouchEnd = (e: TouchEvent): void => {
    if (!this.tracking) return;
    this.tracking = false;

    const wasHorizontal = this.axis === 'x';
    const dx = e.changedTouches[0].clientX - this.startX;
    this.axis = null;
    this.springBack();

    if (!wasHorizontal) return;

    // A horizontal drag is never a tap — stop the card's click handler firing.
    this.armClickGuard();

    if (Math.abs(dx) < SwipeDirective.THRESHOLD) return;
    const host = this.el.nativeElement;
    this.zone.run(() => {
      if (dx < 0) this.swipeLeft.emit(host);
      else this.swipeRight.emit(host);
    });
  };

  private onTouchCancel = (): void => {
    this.tracking = false;
    this.axis = null;
    this.springBack();
  };

  private onClickCapture = (e: MouseEvent): void => {
    if (!this.clickGuardTimer) return;
    e.stopPropagation();
    e.preventDefault();
    this.clearClickGuard();
  };

  private armClickGuard(): void {
    this.clearClickGuard();
    // Fallback expiry — the synthetic click may never arrive (e.g. scrolled away).
    this.clickGuardTimer = setTimeout(() => (this.clickGuardTimer = undefined), 400);
  }

  private clearClickGuard(): void {
    if (this.clickGuardTimer) clearTimeout(this.clickGuardTimer);
    this.clickGuardTimer = undefined;
  }

  /**
   * Eases the dragged host back to rest, then strips the inline transform
   * entirely — a lingering `transform` would make the host a containing block
   * for any fixed-position descendant (dropdowns, popovers) and shift them.
   */
  private springBack(): void {
    if (!this.offsetApplied) return;
    this.offsetApplied = false;

    const host = this.el.nativeElement;
    host.style.transition = SwipeDirective.SPRING_BACK;
    host.style.transform = 'translate3d(0, 0, 0)';

    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      this.settleTimer = undefined;
      host.style.transition = '';
      host.style.transform = '';
    }, SwipeDirective.SPRING_BACK_MS);
  }
}
